import React, { useMemo } from "react";
import {
  AnswerReviewDialog,
  normalizePublicMockTestAttempt,
} from "@/components/common";
import { useGetPublicMockTestAttemptDetailQuery } from "@/store/api/user/publicMockTestApi";
import { buildPublicMockHeaderChips } from "./partMeta";

/**
 * PublicMockTestResultDetailDialog — full answer-review popup for a persisted
 * public mock test attempt.
 *
 * Given a history row (`attempt`, carrying `attemptId`), it fetches the rich
 * per-question detail via `GET /exams/public/history/:attemptId`, normalizes it
 * with `normalizePublicMockTestAttempt`, and renders the shared
 * <AnswerReviewDialog /> — the same review UI used by the Exercise / Exam
 * Statistics tabs. For every question this shows the question text + choices,
 * the user's selected answer, the correct answer, and a correctness indicator,
 * grouped by part (with audio / image / passage context where present).
 */
const PublicMockTestResultDetailDialog = ({ open, onClose, attempt }) => {
  const attemptId = attempt?.attemptId;

  const { data: detail, isFetching } = useGetPublicMockTestAttemptDetailQuery(
    attemptId,
    { skip: !open || !attemptId }
  );

  const detailedGroups = useMemo(
    () => normalizePublicMockTestAttempt(detail),
    [detail]
  );

  if (!attempt) return null;

  const mode = detail?.mode || attempt.mode;
  const isPart = mode === "part";
  const examTitle = detail?.examTitle || attempt.examTitle || "Mock test";

  const headerChips = buildPublicMockHeaderChips({
    isPart,
    score: detail?.score ?? attempt.score ?? 0,
    listeningScore:
      detail?.listeningScore ?? attempt.listeningScore ?? 0,
    readingScore: detail?.readingScore ?? attempt.readingScore ?? 0,
    parts: detail?.selectedParts || attempt.selectedParts || [],
  });

  return (
    <AnswerReviewDialog
      open={open}
      onClose={onClose}
      title={`${examTitle} — Detailed Results`}
      subtitle={isPart ? "Practice by part" : "Full test"}
      headerChips={headerChips}
      detailedGroups={detailedGroups}
      isLoading={isFetching}
      emptyMessage="Detailed answers aren't available for this attempt."
    />
  );
};

export default PublicMockTestResultDetailDialog;
